// pages/sections/ClientContactSection.js
// Client Contact Data — cross-vendor comparison with inline row expansion.
//
// Main grid: one row per PE client with summary columns + a chevron that
// expands a sub-row showing four side-by-side mini-cards (PE, Knuula, CCH,
// SafeSend). Mismatched fields on the vendor cards highlight in yellow;
// clicking a highlighted cell records the reviewer's "this source is correct"
// pick (resolve-field). Mark Reviewed lives on the main row and preserves the
// pull-to-self semantics from the other sections.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../auth/AuthContext';
import { usePeriod } from '../../hooks/usePeriod';
import { useSectionData } from '../../hooks/useSectionData';
import { useRowMutations } from '../../hooks/useRowMutations';
import { useTourDemo } from '../../lib/tourDemo';
import { CLIENT_CONTACT_DEMO } from './clientContactDemoData';
import LastRefreshed from '../../components/LastRefreshed';
import ToggleSwitch from './ToggleSwitch';
import RoleToggle from './RoleToggle';
import ClientContactCompareCards from './ClientContactCompareCards';
import NameAliasesModal from './NameAliasesModal';
import DisengagementModal from './DisengagementModal';
import NlcRequestedBadge from './NlcRequestedBadge';
import {
  nlcModalProps, announceNlcOutcome, nlcLeavesList, requestFlagFrom, withdrawNlcRequest, sweepRelated,
} from './nlcRequestUi';
import { readError, sortByTier } from './reviewUtil';
import { ElSignerInfoModal, ElSignerFormModal } from './ClientContactElSigner';
import { isValidEmail } from './ClientContactCreateRecord';
import { augmentOn } from '../sectionAugments';
import './ReviewTable.css';
import './ClientContactDrawer.css';

const PAGE_SIZE = 25;

// Error toasts stay long enough to read the server's reason.
const ERR_TOAST = { autoClose: 10000 };

// Error types for the multi-select filter. Keys match the chip keys returned
// by rowErrors(), so filtering reuses that single source of truth.
const ERROR_TYPE_OPTIONS = [
  { key: 'email',          label: 'Invalid email' },
  { key: 'spouse',         label: 'Spouse email required' },
  { key: 'mismatch',       label: 'Mismatches' },
  { key: 'spouseMismatch', label: 'Spouse email mismatch' },
];

// Compact checkbox dropdown: a trigger showing the current selection that opens
// a checkbox panel. Closes on outside-click / Escape. Empty = no filter.
function ErrorTypeFilter({ options, selected, onToggle, counts }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey  = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('mousedown', onDown); window.removeEventListener('keydown', onKey); };
  }, [open]);
  const summary = selected.length === 0
    ? 'All errors'
    : selected.length === 1
      ? (options.find(o => o.key === selected[0])?.label || '1 type')
      : `${selected.length} error types`;
  return (
    <div className="cc-ms" ref={ref}>
      <button type="button" className="cc-ms-trigger" onClick={() => setOpen(o => !o)}>
        <span className="cc-ms-summary">{summary}</span>
        <span className="cc-ms-caret">▾</span>
      </button>
      {open && (
        <div className="cc-ms-panel">
          {options.map(o => (
            <label key={o.key} className="cc-ms-option">
              <input type="checkbox" checked={selected.includes(o.key)} onChange={() => onToggle(o.key)} />
              {' '}{o.label} ({(counts && counts[o.key]) || 0})
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// Count of mismatched fields that still need reviewer attention (i.e., a
// vendor disagrees with PE AND the reviewer hasn't picked a winning source).
// The server returns `mismatchCount` as the raw disagreement total; we derive
// an "unresolved" count here so resolving a field is reflected immediately in
// the row badge, the summary stat, and the Mismatches-only filter.
function unresolvedCount(row) {
  if (!row?.fields) return 0;
  // Paper EL clients get paper engagement letters — email is irrelevant, so the
  // email field's disagreement doesn't count as a mismatch to resolve.
  const paper = row.isPaper === true;
  return Object.entries(row.fields)
    .filter(([k, f]) => f.mismatch && !f.resolvedSource && !(paper && k === 'email'))
    .length;
}

// All current errors on a row, as chips for the "Errors" column. Two kinds:
// 'invalid' (red — bad/missing email or spouse email) and 'mismatch' (amber —
// a single rolled-up count of vendor disagreements not yet resolved). Chips
// fall off automatically as the reviewer fixes the email / resolves mismatches.
// Paper EL suppresses every email-related error (invalid email, spouse email
// required, spouse-email mismatch, and the email field mismatch) — a paper
// client doesn't need email on file.
// True when the reviewer has picked a vendor's email as correct (resolve-field)
// AND that chosen email is valid (1b). Lets a valid pick clear the "invalid
// email" error even though PE's own ContEmail is bad. SafeSend cells hold a
// list; any valid one counts.
function chosenEmailValid(row) {
  const f = row.fields?.email;
  if (!f || !f.resolvedSource) return false;
  const src = f.resolvedSource;
  if (src === 'safesend') {
    const list = Array.isArray(f.safesend) ? f.safesend : [];
    return list.some(isValidEmail);
  }
  return isValidEmail(f[src]);
}

// "Hard" contact errors that block Mark Reviewed for BOTH roles: an invalid/
// missing primary email (unless a valid one was chosen — 1b) or an invalid/
// missing MFJ spouse email. Paper-EL clients need no email at all. (The
// "Different Signer" record is optional supplemental data — it never blocks.)
function contactHardInvalid(row) {
  if (row.createdRecord) return false;        // an authored record settles the contact
  if (row.isPaper === true) return false;
  const emailBad  = row.emailValid === false && !chosenEmailValid(row);
  const spouseBad = row.spouseEmailValid === false;
  return emailBad || spouseBad;
}

function rowErrors(row) {
  const chips = [];
  const paper   = row.isPaper === true;
  const created = !!row.createdRecord;
  // A reviewer-authored record supplies the corrected CONTACT values, so it
  // settles the email/spouse errors — but it does NOT cover Manager/Partner
  // (staffing, not part of the record). Those mismatches still surface below via
  // the mismatch count (the backend leaves name-field mismatches intact for a
  // created record) and must still be resolved with a source pick.
  if (!created && !paper && row.emailValid === false && !chosenEmailValid(row))
    chips.push({ key: 'email', kind: 'invalid', label: 'Invalid email' });
  if (!created && !paper && row.spouseEmailValid === false)
    chips.push({ key: 'spouse', kind: 'invalid', label: 'Spouse email required' });
  const mism = unresolvedCount(row);
  if (mism > 0)                                 chips.push({ key: 'mismatch', kind: 'mismatch', label: `${mism} mismatch${mism === 1 ? '' : 'es'}` });
  // SafeSend spouse-email vs PE (only when the feed carries spouse emails).
  if (!created && !paper && row.spouseEmailMismatch === true) chips.push({ key: 'spouseMismatch', kind: 'mismatch', label: 'Spouse email mismatch' });
  return chips;
}

export default function ClientContactSection() {
  const { isAdmin, email, impersonating } = useAuth();
  // Spotlight-tour demo mode: serve curated fixtures instead of /api/diClientContact
  // and short-circuit every write, so a curious click never mutates prod.
  const demo = useTourDemo();
  const { period, periodActive, sections } = usePeriod();

  // "New client import" augment: adds the Paper EL + Mark NLC columns.
  const ccConfig = (sections || []).find(s => s.sectionKey === 'client-contact-data')?.config || {};
  const showNewClientImport = augmentOn(ccConfig, 'newClientImport');
  // Only a real admin, on a period with this augment, can see/reverse NLC rows.
  const canManageNlc = isAdmin && showNewClientImport;
  // "CCH reporting-only" augment: the backend already excludes CCH from mismatch
  // calcs; this just drives the informational banner.
  const cchReportingOnly = augmentOn(ccConfig, 'cchReportingOnly');

  const [activeRole,   setActiveRole]   = useState('manager');
  const [search,       setSearch]       = useState('');
  const [officeFilter, setOfficeFilter] = useState('');
  const [errorTypes,  setErrorTypes]    = useState([]);   // multi-select: error-type keys
  const [showNlc,      setShowNlc]      = useState(false); // admin: show NLC'd clients
  const [nlcModalRow,  setNlcModalRow]  = useState(null);  // row pending the NLC disengagement modal
  const [nlcSaving,    setNlcSaving]    = useState(false);
  const [nlcError,     setNlcError]     = useState(null);  // last failed NLC submit — shown in the modal
  const [page,         setPage]         = useState(1);
  const [aliasOpen,    setAliasOpen]    = useState(false); // admin: name-alias editor
  const [elInfoOpen,   setElInfoOpen]   = useState(false); // Different Signer definitions modal
  const [elModalFor,   setElModalFor]   = useState(null);  // contIndex whose Different Signer form is open

  const toggleErrorType = useCallback((key) => {
    setErrorTypes(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
    setPage(1);
  }, []);
  const [expanded,     setExpanded]     = useState(new Set());

  const fetcher = useCallback(async () => {
    if (demo) return CLIENT_CONTACT_DEMO;   // tour: curated fixtures, no fetch
    if (!period) return null;
    const res = await fetch(`/api/diClientContact/${period}`, { credentials: 'include' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }, [period, demo]);

  // Optimistic saves: ordered per client (rapid clicks reach the server in order), controls stay live,
  // and a refetch/poll landing mid-save can't overwrite an edited row with older server data.
  const mutations = useRowMutations(r => r.contIndex);

  // The app heartbeat (every 15 s) refetches when another reviewer's audit change
  // (signoffs, resolutions, created records) or a rebuilt PE snapshot lands.
  // Silent (no spinner); optimistic local patches are reconciled on each refetch.
  // Off during the tour — there's no live data to refresh.
  const { data, setData, initialLoading, error, lastRefreshedAt, freshness, silentRefetch }
    = useSectionData(fetcher, { deps: [period, email, demo], reconcile: mutations.reconcile,
        heartbeat: demo ? null : { sections: ['client-contact-data'], period } });

  // ── local-state patcher ────────────────────────────────────────────────
  function patchRow(contIndex, updater) {
    setData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        rows: prev.rows.map(r => (String(r.contIndex) === String(contIndex) ? updater(r) : r)),
      };
    });
  }

  // The row as rendered right now — the state to revert to (never read it out of a setData updater,
  // React may run that later).
  const rowFor = (contIndex) => (data?.rows || []).find(r => String(r.contIndex) === String(contIndex)) || null;

  // Optimistic write for one client: patch the row now, queue `task` behind that client's other saves,
  // and on failure put the row back and toast the server's reason (long enough to read). If other saves
  // were queued behind it the page refetches once they drain instead. Resolves true on success.
  async function mutate(contIndex, patch, task, failMsg, snapshot = rowFor(contIndex)) {
    patchRow(contIndex, patch);
    try {
      await mutations.run(contIndex, task, {
        revert: () => { if (snapshot) patchRow(contIndex, () => snapshot); },
        refetch: silentRefetch,
      });
      return true;
    } catch (err) {
      toast.error(`${failMsg}: ${err.message}`, ERR_TOAST);
      return false;
    }
  }

  // ── API calls ──────────────────────────────────────────────────────────
  async function fireMarkReviewed(contIndex, role) {
    if (demo) return;   // tour demo — never POST
    const res = await fetch(`/api/diClientContact/${period}`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'mark-reviewed', contIndex, role }),
    });
    if (!res.ok) {
      // Surface the server's reason (e.g. the 2b invalid-contact block at 422).
      let msg = `HTTP ${res.status}`;
      try { const j = await res.json(); if (j && j.error) msg = j.error; } catch { /* keep status */ }
      throw new Error(msg);
    }
  }

  async function fireCreateRecord(contIndex, record) {
    if (demo) return {};   // tour demo — never POST
    const res = await fetch(`/api/diClientContact/${period}`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'create-record', contIndex, record }),
    });
    if (!res.ok) throw new Error(await readError(res));
    return res.json();
  }

  async function fireClearRecord(contIndex) {
    if (demo) return;   // tour demo — never POST
    const res = await fetch(`/api/diClientContact/${period}`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'clear-record', contIndex }),
    });
    if (!res.ok) throw new Error(await readError(res));
  }

  // ── New client import augment: Paper EL toggle + Mark NLC ────────────────
  async function firePost(payload) {
    if (demo) return {};   // tour demo — never POST
    const res = await fetch(`/api/diClientContact/${period}`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(await readError(res));   // the endpoint answers { error }
    return res.json().catch(() => ({}));
  }

  function setPaper(contIndex, value) {
    return mutate(contIndex, r => ({ ...r, isPaper: value }),
      () => firePost({ action: 'set-attribute', contIndex, key: 'isPaper', value }),
      'Failed to update Paper EL');
  }

  // "Different Signer" (optional). Stores a full separate signer-contact record
  // as JSON. `record === null` clears it. Never gates review — purely
  // supplemental data on the client.
  async function setElSignerRecord(contIndex, record) {
    const hasRecord = !!(record && Object.keys(record).length);
    const ok = await mutate(contIndex, r => ({ ...r, elSignerRecord: record, elSignerHasRecord: hasRecord }),
      () => firePost({ action: 'set-attribute', contIndex, key: 'elSignerRecord', value: hasRecord ? JSON.stringify(record) : null }),
      'Failed to save Different Signer');
    if (ok && !demo) toast.success(hasRecord ? 'Different Signer saved.' : 'Different Signer cleared.');
  }

  // Open the disengagement follow-up modal for this client. The actual mark
  // happens in confirmNlc once the reviewer answers the disengagement questions.
  function markNlc(contIndex) {
    const row = (data?.rows || []).find(r => String(r.contIndex) === String(contIndex));
    if (row) { setNlcError(null); setNlcModalRow(row); }
  }

  // The client's Partner (or an unimpersonated admin) marks it now; anyone else's mark is a request.
  const nlcActsDirectly = (row) =>
    (isAdmin && !impersonating) || (!!row.partnerEmail && row.partnerEmail.toLowerCase() === email);

  // The viewer's standing on the row is known (`nlcActsDirectly` also words the modal), so the row is
  // patched at once — 'direct': flagged NLC (it drops out of the active list; admins still see it under
  // "Show NLC"); 'request': flagged "NLC requested" until the Partner confirms — and put back if the
  // server refuses. The modal stays open until the answer so its inline error keeps the reviewer's
  // answers. The server's `outcome` has the last word (e.g. 'already_nlc' / 'already_requested').
  async function confirmNlc(row, disengagement) {
    if (demo) { setNlcModalRow(null); return; }   // tour demo — never POST
    const contIndex = row.contIndex;
    const direct = nlcActsDirectly(row);
    setNlcSaving(true);
    setNlcError(null);
    patchRow(contIndex, r => (direct
      ? { ...r, isNlc: true }
      : { ...r, nlcRequest: r.nlcRequest || requestFlagFrom(null, email) }));
    let j;
    try {
      j = await mutations.run(contIndex, () => firePost({
        action: 'mark-nlc',
        contIndex,
        clientCode: row.clientCode,
        clientName: row.clientName,
        disengagement,
      }), {
        revert: () => patchRow(contIndex, () => row),
        refetch: silentRefetch,
      });
    } catch (err) {
      setNlcError(err.message);
      toast.error(`Failed to mark NLC: ${err.message}`, ERR_TOAST);
      setNlcSaving(false);
      return;
    }
    setNlcModalRow(null);
    setNlcSaving(false);
    const outcome = announceNlcOutcome(j, row.clientCode, 'Marked No Longer Client.');
    if (nlcLeavesList(outcome)) {
      patchRow(contIndex, r => ({ ...r, isNlc: true, nlcRequest: null }));
    } else {
      // Keep the flag, with the server's request id so Withdraw works (and undo a too-eager NLC flag).
      patchRow(contIndex, r => ({ ...r, isNlc: !!row.isNlc, nlcRequest: { ...(r.nlcRequest || {}), ...requestFlagFrom(j, email) } }));
    }
    await sweepRelated({
      disengagement, primaryContIndex: contIndex, primaryClientCode: row.clientCode, period,
      sourceSectionKey: 'client-contact-data',
    });
    silentRefetch();
  }

  // The requester takes a pending request back: the badge goes at once; a failure restores it.
  async function withdrawNlc(row) {
    const req = row.nlcRequest;
    if (!req || demo) return;
    patchRow(row.contIndex, r => ({ ...r, nlcRequest: null }));
    try {
      await withdrawNlcRequest(req.id);
      toast.success(`Request for ${row.clientCode} withdrawn.`);
    } catch (err) {
      patchRow(row.contIndex, r => ({ ...r, nlcRequest: req }));
      toast.error(`Couldn't withdraw the request for ${row.clientCode}: ${err.message}`, ERR_TOAST);
    }
  }

  // Admin-only: reverse an NLC mark, restoring the client everywhere.
  async function unmarkNlc(contIndex) {
    const ok = await mutate(contIndex, r => ({ ...r, isNlc: false }),
      () => firePost({ action: 'unmark-nlc', contIndex }), 'Failed to unmark NLC');
    if (ok) {
      toast.success('NLC removed — client restored.');
      silentRefetch();
    }
  }

  async function fireResolveField(contIndex, fieldKey, source) {
    if (demo) return;   // tour demo — never POST
    const res = await fetch(`/api/diClientContact/${period}`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'resolve-field', contIndex, fieldKey, source }),
    });
    if (!res.ok) throw new Error(await readError(res));
  }

  async function fireUnresolveField(contIndex, fieldKey) {
    if (demo) return;   // tour demo — never POST
    const res = await fetch(`/api/diClientContact/${period}`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'unresolve-field', contIndex, fieldKey }),
    });
    if (!res.ok) throw new Error(await readError(res));
  }

  // ── pull-to-self + cascade ─────────────────────────────────────────────
  async function pullToSelf(contIndex) {
    const stamp = { reviewedAt: new Date().toISOString(), reviewedBy: 'You' };
    let wroteManager = false;
    const ok = await mutate(contIndex, r => ({
      ...r,
      reviews: { ...(r.reviews || {}), manager: stamp, partner: stamp },
    }), async () => {
      await fireMarkReviewed(contIndex, 'manager');
      wroteManager = true;
      await fireMarkReviewed(contIndex, 'partner');
    }, 'Pull failed');
    if (ok) toast.success('Pulled to partner (manager + partner both marked).');
    else if (wroteManager) silentRefetch();    // the manager sign-off did land — show the server's truth
  }

  async function markReviewed(contIndex) {
    const initialRow = rowFor(contIndex);
    if (!initialRow) return;
    // 2b — don't even fire the request for a hard-invalid contact (server also
    // blocks): invalid/missing email (unless a valid one was chosen — 1b),
    // invalid MFJ spouse email, or a missing EL signer email (1c). Paper EL
    // clients are exempt.
    if (contactHardInvalid(initialRow)) {
      toast.error('Fix the flagged email(s) before marking this client reviewed.');
      return;
    }
    // 1a — a PARTNER can't mark reviewed while any error remains on the row
    // (unresolved mismatch, spouse mismatch, etc.). Managers may still sign off.
    if (activeRole === 'partner' && rowErrors(initialRow).length > 0) {
      toast.error('Resolve all errors on this client before marking it reviewed.');
      return;
    }
    if (activeRole === 'partner' && !initialRow.reviews?.manager) return pullToSelf(contIndex);

    const me  = (email || '').toLowerCase();
    const mgr = (initialRow.managerEmail || '').toLowerCase();
    const ptr = (initialRow.partnerEmail || '').toLowerCase();
    const isBothRoles = mgr === me && ptr === me && !!me;
    const otherRole = activeRole === 'manager' ? 'partner' : 'manager';
    const cascadeNeeded = isBothRoles && !initialRow.reviews?.[otherRole];

    const stamp = { reviewedAt: new Date().toISOString(), reviewedBy: 'You' };
    let cascadeErr = null;
    const ok = await mutate(contIndex, r => ({
      ...r,
      reviews: {
        ...(r.reviews || {}),
        [activeRole]: stamp,
        ...(cascadeNeeded ? { [otherRole]: stamp } : {}),
      },
    }), async () => {
      await fireMarkReviewed(contIndex, activeRole);
      if (cascadeNeeded) {
        // The primary sign-off landed; a failed cascade is reported, not rolled back.
        try { await fireMarkReviewed(contIndex, otherRole); } catch (err) { cascadeErr = err; }
      }
    }, 'Failed', initialRow);
    if (!ok) return;
    if (cascadeErr) {
      patchRow(contIndex, r => ({
        ...r,
        reviews: { ...(initialRow.reviews || {}), [activeRole]: stamp },
      }));
      toast.warning(`Primary review saved; cascade to ${otherRole} failed: ${cascadeErr.message}`, ERR_TOAST);
    } else {
      toast.success(cascadeNeeded ? 'Marked reviewed (manager + partner)' : 'Marked reviewed');
    }
  }

  async function fireAdminUnmark(contIndex, role) {
    if (demo) return;   // tour demo — never POST
    const res = await fetch(`/api/diAdminUnmark/${period}/client-contact-data`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ recordKey: String(contIndex), role }),
    });
    if (!res.ok) throw new Error(await readError(res));
  }

  async function adminUnmark(contIndex) {
    if (!isAdmin) return;
    const row = rowFor(contIndex);
    if (!row) return;
    // Cascade-unmark mirrors the cascade-mark: when the same user holds both
    // manager and partner on this client, clearing one signoff would leave
    // the other intact and out of sync — so we clear both together.
    const mgrEm = (row.managerEmail || '').toLowerCase();
    const ptrEm = (row.partnerEmail || '').toLowerCase();
    const cascade = !!mgrEm && mgrEm === ptrEm;

    const confirmMsg = cascade
      ? `Unmark this contact as reviewed? Manager and Partner are the same user, so both signoffs will be cleared.`
      : `Unmark this contact as reviewed for ${activeRole}?`;
    if (!window.confirm(confirmMsg)) return;

    let firstDone = false;
    const ok = await mutate(contIndex, r => {
      // Unmarking the manager also clears the partner's sign-off server-side (it rests on the manager's).
      if (cascade || activeRole === 'manager') return { ...r, reviews: {} };
      const { [activeRole]: _dropped, ...rest } = r.reviews || {};
      return { ...r, reviews: rest };
    }, async () => {
      if (cascade) {
        await fireAdminUnmark(contIndex, 'manager');
        firstDone = true;
        await fireAdminUnmark(contIndex, 'partner');
      } else {
        await fireAdminUnmark(contIndex, activeRole);
      }
    }, 'Failed to unmark', row);
    if (ok) toast.success(cascade ? 'Unmarked reviewed (manager + partner).' : 'Unmarked reviewed.');
    else if (firstDone) silentRefetch();       // half the cascade landed — show the server's truth
  }

  // ── resolve / unresolve a single field ─────────────────────────────────
  async function resolveField(contIndex, fieldKey, source) {
    const ok = await mutate(contIndex, r => ({
      ...r,
      fields: { ...r.fields, [fieldKey]: { ...r.fields[fieldKey], resolvedSource: source } },
    }), () => fireResolveField(contIndex, fieldKey, source), 'Failed to resolve');
    if (ok) toast.success(`${fieldKey}: marked ${source.toUpperCase()} as correct.`);
  }

  async function unresolveField(contIndex, fieldKey) {
    const ok = await mutate(contIndex, r => ({
      ...r,
      fields: { ...r.fields, [fieldKey]: { ...r.fields[fieldKey], resolvedSource: null } },
    }), () => fireUnresolveField(contIndex, fieldKey), 'Failed to clear the resolution');
    if (ok) toast.success(`${fieldKey}: cleared resolution.`);
  }

  // ── create / clear a reviewer-authored contact record (2c) ──────────────
  // Validity (2b) depends on the effective email, which a created record can
  // change — recompute it optimistically so Mark Reviewed unblocks instantly,
  // then silentRefetch for the authoritative server values.
  function recomputeValidity(row, createdRecord) {
    const email  = (createdRecord && createdRecord.email) || row.vendors?.pe?.email || '';
    const spouse = (createdRecord && createdRecord.spouseEmail) || row.spouseEmail || '';
    const emailValid = isValidEmail(email);
    // SafeSend can satisfy the MFJ spouse requirement (server sets spouseFromSafesend).
    const spouseEmailValid = !row.isMFJ || isValidEmail(spouse) || row.spouseFromSafesend === true;
    return { emailValid, spouseEmailValid, contactValid: emailValid && spouseEmailValid };
  }

  async function createRecord(contIndex, record) {
    const ok = await mutate(contIndex, r => ({ ...r, createdRecord: record, ...recomputeValidity(r, record) }),
      () => fireCreateRecord(contIndex, record), 'Failed to save record');
    if (ok) {
      toast.success('Contact record saved.');
      silentRefetch();
    }
  }

  async function clearRecord(contIndex) {
    const ok = await mutate(contIndex, r => ({ ...r, createdRecord: null, ...recomputeValidity(r, null) }),
      () => fireClearRecord(contIndex), 'Failed to clear record');
    if (ok) {
      toast.success('Contact record cleared.');
      silentRefetch();
    }
  }

  // ── derived view state ─────────────────────────────────────────────────
  const offices = useMemo(() => {
    if (!data) return [];
    return [...new Set(data.rows.map(r => r.clientOffice))].filter(Boolean).sort();
  }, [data]);

  // Office-wide visibility augment (2a): when the server says office-wide is in
  // effect for this caller, it has already scoped data.rows to the office(s) and
  // any in-office user may review any of them — so we DON'T narrow to the
  // caller's own manager/partner rows.
  const officeWide = !!data?.officeWide;

  // Client-side role filter — normally a user sees a client in manager view only
  // when they're the assigned manager, and in partner view only when they're the
  // assigned partner (firm-wide visibility otherwise lives in Steering). Under
  // office-wide scope this filter is relaxed to the full server-scoped set.
  const roleRows = useMemo(() => {
    if (!data) return [];
    // Tour demo shows every fixture row regardless of who's signed in.
    if (officeWide || demo) return data.rows;
    const me = (email || '').toLowerCase();
    const emailField = activeRole === 'manager' ? 'managerEmail' : 'partnerEmail';
    return data.rows.filter(r => (r[emailField] || '').toLowerCase() === me);
  }, [data, email, activeRole, officeWide, demo]);

  // NLC rows only appear for admins (backend include path). They stay OUT of the
  // active review population + every count; admins reveal them with "Show NLC"
  // to reverse a mark. Flipping isNlc (mark/unmark) moves a row between these
  // sets, so the counter cards update immediately.
  const activeRows = useMemo(() => roleRows.filter(r => !r.isNlc), [roleRows]);
  const nlcRows    = useMemo(() => roleRows.filter(r => r.isNlc),  [roleRows]);
  const baseRows   = showNlc ? nlcRows : activeRows;

  // Per-error-type row counts (over active rows, before the error filter) so
  // each picklist option shows how many clients have that error.
  const errorTypeCounts = useMemo(() => {
    const counts = {};
    for (const o of ERROR_TYPE_OPTIONS) counts[o.key] = 0;
    for (const r of activeRows) {
      for (const c of rowErrors(r)) {
        if (counts[c.key] != null) counts[c.key] += 1;
      }
    }
    return counts;
  }, [activeRows]);

  const filtered = useMemo(() => {
    let out = baseRows;
    if (search) {
      const s = search.toLowerCase();
      out = out.filter(r =>
        (r.clientCode || '').toLowerCase().includes(s) ||
        (r.clientName || '').toLowerCase().includes(s)
      );
    }
    if (officeFilter)  out = out.filter(r => r.clientOffice === officeFilter);
    if (errorTypes.length) {
      const sel = new Set(errorTypes);
      out = out.filter(r => rowErrors(r).some(c => sel.has(c.key)));
    }
    // Manager view: open → reviewed. Partner view: manager-reviewed (ready for me) → not yet
    // manager-reviewed → signed off by me. Each tier by ClientCode ascending.
    return sortByTier(out, r => {
      if (r.reviews?.[activeRole]) return 2;
      if (activeRole !== 'partner') return 0;
      return r.reviews?.manager ? 0 : 1;
    });
  }, [baseRows, search, officeFilter, errorTypes, activeRole]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const paged      = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  function toggleExpanded(contIndex) {
    setExpanded(prev => {
      const n = new Set(prev);
      const k = String(contIndex);
      if (n.has(k)) n.delete(k); else n.add(k);
      return n;
    });
  }

  // Only show the full-screen loading/error state on the FIRST load (no data
  // yet). Once we have data, every refresh — the 15s poll, a silent refetch, or
  // even a transient poll error — updates in place: the table + dropdowns stay
  // mounted (no blank flash, open filters persist), and stale data shows until
  // the fresh data swaps in.
  if (initialLoading) return <div className="rt-loading">Loading client contacts…</div>;
  if (error && !data)   return <div className="rt-error">Error: {error}</div>;
  if (!data)   return null;

  const reviewedCount    = activeRows.filter(r => !!r.reviews?.[activeRole]).length;
  // "Needs Review" now counts rows that carry at least one Errors-column entry
  // (mismatch, invalid email, or missing spouse email).
  const errorRowCount    = activeRows.filter(r => rowErrors(r).length > 0).length;
  // Partner-only: rows where the manager has signed off but the partner
  // hasn't yet — the actionable queue for the current user.
  const readyForReview   = activeRole === 'partner'
    ? activeRows.filter(r => !!r.reviews?.manager && !r.reviews?.partner).length
    : 0;
  const readOnly         = !periodActive && !demo;   // tour keeps controls live (writes are guarded)
  const compareFields    = data?.compareFields || [];

  return (
    <div className="rt-page">
      <div className="rt-header">
        <div className="rt-title-block">
          <h2 className="rt-title">Client Contact Data</h2>
          {readOnly && <span className="rt-readonly-badge">Read-only</span>}
          <LastRefreshed at={lastRefreshedAt} freshness={freshness} onRefresh={silentRefetch} />
        </div>
        <div className="rt-header-actions">
          {isAdmin && (
            <button type="button" className="cc-alias-btn" onClick={() => setAliasOpen(true)}
              title="Manage name aliases — equate names for the same person (e.g. after a name change) so they don't flag as a Manager mismatch.">
              <i className="fas fa-user-pen" aria-hidden="true" /> Name aliases
            </button>
          )}
          <RoleToggle role={activeRole} onChange={setActiveRole} disabled={initialLoading} />
        </div>
      </div>

      {isAdmin && (
        <NameAliasesModal
          open={aliasOpen}
          onClose={() => setAliasOpen(false)}
          onSaved={silentRefetch}
        />
      )}

      <ElSignerInfoModal open={elInfoOpen} onClose={() => setElInfoOpen(false)} />

      {nlcModalRow && (
        <DisengagementModal
          client={{ code: nlcModalRow.clientCode, name: nlcModalRow.clientName }}
          {...nlcModalProps(nlcActsDirectly(nlcModalRow) ? 'direct' : 'request')}
          submitting={nlcSaving}
          error={nlcError}
          onCancel={() => setNlcModalRow(null)}
          onConfirm={(disengagement) => confirmNlc(nlcModalRow, disengagement)}
        />
      )}

      {elModalFor != null && (
        <ElSignerFormModal
          row={(data.rows || []).find(r => String(r.contIndex) === String(elModalFor)) || null}
          readOnly={readOnly}
          onClose={() => setElModalFor(null)}
          onSave={(record) => { setElSignerRecord(elModalFor, record); setElModalFor(null); }}
        />
      )}

      <div className="cc-help-note">
        <i className="fas fa-circle-info" aria-hidden="true" />
        <span>
          Need to change a contact field? Open a client and use <strong>Create New Record</strong> in the
          drilldown to enter the corrected values — this section doesn't edit a source directly; your changes
          are saved as a new record.
        </span>
      </div>

      {cchReportingOnly && (
        <div className="cc-cch-note" role="note">
          <i className="fas fa-circle-info" aria-hidden="true" />
          <span>CCH data for reporting purposes. No contact data will be imported into CCH.</span>
        </div>
      )}

      <div className="rt-summary" data-tour="cc-summary">
        <div className="rt-stat"><span className="rt-stat-val">{activeRows.length}</span><span className="rt-stat-lbl">{officeWide ? 'Office Contacts' : 'Your Contacts'}</span></div>
        <div className="rt-stat rt-stat--pending"
             title="Rows with at least one error (mismatch, invalid email, or missing spouse email).">
          <span className="rt-stat-val">{errorRowCount}</span><span className="rt-stat-lbl">Needs Review</span></div>
        <div className="rt-stat rt-stat--done"><span className="rt-stat-val">{reviewedCount}</span><span className="rt-stat-lbl">Reviewed</span></div>
        {activeRole === 'partner' && (
          <div className="rt-stat rt-stat--ready"
               title="Manager has signed off; awaiting your partner review.">
            <span className="rt-stat-val">{readyForReview}</span>
            <span className="rt-stat-lbl">Ready for Review</span>
          </div>
        )}
      </div>

      <div className="rt-filters" data-tour="cc-filters">
        <input className="rt-filter-input" placeholder="Search code or name…"
               value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        <select className="rt-filter-select" value={officeFilter}
                onChange={e => { setOfficeFilter(e.target.value); setPage(1); }}>
          <option value="">All Offices</option>
          {offices.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        <ErrorTypeFilter
          options={ERROR_TYPE_OPTIONS}
          selected={errorTypes}
          onToggle={toggleErrorType}
          counts={errorTypeCounts}
        />
        {canManageNlc && (
          <label className="cc-filter-check"
                 title="Show clients marked No Longer Client (admin) so they can be reversed. They stay excluded from every section otherwise.">
            <input type="checkbox" checked={showNlc}
                   onChange={e => { setShowNlc(e.target.checked); setPage(1); }} />
            Show NLC ({nlcRows.length})
          </label>
        )}
        {(search || officeFilter || errorTypes.length > 0) && (
          <button
            type="button"
            className="rt-filter-clear"
            onClick={() => {
              setSearch('');
              setOfficeFilter('');
              setErrorTypes([]);
              setPage(1);
            }}
            title="Reset search, office, and error-type filters"
          >
            Clear filters
          </button>
        )}
      </div>

      {activeRole === 'partner' && activeRows.some(r => !r.reviews?.manager) && (
        <div className="rt-partner-note">
          <i className="fas fa-info-circle" />
          <span>
            If your manager hasn't reviewed a client yet, you can still mark it reviewed —
            your action will sign off as both Manager and Partner.
          </span>
        </div>
      )}

      <div className="rt-table-wrap cc-table-wrap">
        <table className="rt-table cc-drill-table">
          <thead>
            <tr>
              <th className="cc-chevron-col"></th>
              <th>Code</th>
              <th>Client</th>
              <th>Office</th>
              <th>Errors</th>
              <th className="cc-elsigner-col">
                Different Signer{' '}
                <button
                  type="button"
                  className="cc-info-btn"
                  onClick={() => setElInfoOpen(true)}
                  title="What is a Different Signer? (definitions)"
                  aria-label="Different Signer definitions"
                >
                  <i className="fas fa-circle-info" aria-hidden="true" />
                </button>
              </th>
              {showNewClientImport && <th>Paper EL</th>}
              <th>Mark Reviewed</th>
              {showNewClientImport && <th>Mark NLC</th>}
            </tr>
          </thead>
          <tbody>
            {paged.map((row, idx) => {
              const reviewed        = !!row.reviews?.[activeRole];
              const pendingManager  = activeRole === 'partner' && !row.reviews?.manager;
              const partnerSigned   = activeRole === 'manager' && !!row.reviews?.partner;
              const errorChips      = rowErrors(row);
              const hardInvalid     = contactHardInvalid(row);
              // 1a — partner is additionally blocked while ANY error remains.
              const partnerBlocked  = activeRole === 'partner' && errorChips.length > 0;
              // Tour: force the first row open so the compare-cards step has a target.
              const tourFirst       = demo && idx === 0;
              const isExpanded      = expanded.has(String(row.contIndex)) || tourFirst;
              return (
                <React.Fragment key={row.contIndex}>
                  <tr className={`${reviewed ? 'rt-row-reviewed' : ''} ${pendingManager ? 'rt-row-pending-mgr' : ''} ${isExpanded ? 'cc-row-expanded' : ''} ${row.isNlc ? 'cc-row-nlc' : ''}`}
                      data-tour={tourFirst ? 'cc-row' : undefined}>
                    <td className="cc-chevron-col">
                      <button className="cc-chevron-btn"
                              onClick={() => toggleExpanded(row.contIndex)}
                              aria-label={isExpanded ? 'Collapse' : 'Expand'}>
                        {isExpanded ? '▾' : '▸'}
                      </button>
                    </td>
                    <td onClick={() => toggleExpanded(row.contIndex)} style={{cursor: 'pointer'}}>{row.clientCode}</td>
                    <td onClick={() => toggleExpanded(row.contIndex)} style={{cursor: 'pointer'}}>
                      {row.clientName}
                      {row.isNlc && <span className="cc-nlc-tag" title="No Longer Client — excluded from all sections.">NLC</span>}
                    </td>
                    <td>{row.clientOffice}</td>
                    <td>
                      {errorChips.length === 0 ? (
                        <span className="cc-badge cc-badge--ok">
                          {row.mismatchCount === 0 ? 'All match' : 'No errors'}
                        </span>
                      ) : (
                        <div className="cc-error-chips">
                          {errorChips.map(c => (
                            <span key={c.key} className={`cc-chip cc-chip--${c.kind}`}>{c.label}</span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="cc-elsigner-cell" onClick={e => e.stopPropagation()}>
                      <button
                        type="button"
                        className={`cc-elsigner-btn ${row.elSignerHasRecord ? 'cc-elsigner-btn--set' : ''}`}
                        onClick={() => setElModalFor(row.contIndex)}
                        title={row.elSignerHasRecord
                          ? 'A separate signer is on file — view or edit.'
                          : 'Record a separate signer (optional).'}
                      >
                        <i className="fas fa-user-pen" aria-hidden="true" />{' '}
                        {row.elSignerHasRecord ? 'Signer on file' : 'Different Signer'}
                      </button>
                    </td>
                    {showNewClientImport && (
                      <td onClick={e => e.stopPropagation()}>
                        <ToggleSwitch
                          checked={!!row.isPaper}
                          disabled={readOnly}
                          onChange={checked => setPaper(row.contIndex, checked)}
                        />
                      </td>
                    )}
                    <td className="rt-mark-reviewed-cell" data-tour={tourFirst ? 'cc-review' : undefined} onClick={e => e.stopPropagation()}
                        title={activeRole === 'partner' && !reviewed && errorChips.length > 0
                          ? 'Resolve all errors on this client before you can mark it reviewed.' : undefined}>
                      {pendingManager && !reviewed && (
                        <span className="rt-pending-mgr-corner"
                              title="Manager hasn't reviewed yet — marking here will pull the review to you.">
                          Pending Manager
                        </span>
                      )}
                      {partnerSigned && (
                        <span className="rt-partner-signed-corner"
                              title="Partner has already signed off on this client.">
                          Partner Signed Off
                        </span>
                      )}
                      <ToggleSwitch
                        checked={reviewed}
                        disabled={readOnly || row.isNlc || (!reviewed && (hardInvalid || partnerBlocked)) || (!isAdmin && (reviewed || partnerSigned))}
                        onChange={checked => {
                          if (checked && !reviewed) markReviewed(row.contIndex);
                          else if (!checked && reviewed && isAdmin) adminUnmark(row.contIndex);
                        }}
                      />
                    </td>
                    {showNewClientImport && (
                      <td onClick={e => e.stopPropagation()}>
                        {row.isNlc ? (
                          isAdmin ? (
                            <button
                              type="button"
                              className="cc-nlc-btn cc-nlc-btn--undo"
                              disabled={readOnly}
                              onClick={() => unmarkNlc(row.contIndex)}
                              title="Reverse NLC — restores this client across all sections."
                            >
                              Unmark NLC
                            </button>
                          ) : (
                            <span className="cc-nlc-tag">NLC</span>
                          )
                        ) : row.nlcRequest ? (
                          <NlcRequestedBadge request={row.nlcRequest} onWithdraw={() => withdrawNlc(row)} />
                        ) : (
                          <button
                            type="button"
                            className="cc-nlc-btn"
                            disabled={readOnly}
                            onClick={() => markNlc(row.contIndex)}
                            title="Mark No Longer Client — removes this client from every section and period."
                          >
                            Mark NLC
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                  {isExpanded && (
                    <tr className="cc-expanded-row" data-tour={tourFirst ? 'cc-compare' : undefined}>
                      <td colSpan={showNewClientImport ? 9 : 7}>
                        <ClientContactCompareCards
                          row={row}
                          compareFields={compareFields}
                          readOnly={readOnly}
                          onResolve={(fieldKey, source) => resolveField(row.contIndex, fieldKey, source)}
                          onUnresolve={(fieldKey) => unresolveField(row.contIndex, fieldKey)}
                          onCreateRecord={(record) => createRecord(row.contIndex, record)}
                          onClearRecord={() => clearRecord(row.contIndex)}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>

        {filtered.length === 0 && baseRows.length > 0 && (
          <div className="rt-empty">No contacts match.</div>
        )}
        {baseRows.length === 0 && (
          <div className="rt-empty">
            {showNlc ? 'No clients are marked No Longer Client.'
              : officeWide ? 'No contacts in the configured office(s).'
                : 'No contacts assigned to you.'}
          </div>
        )}
      </div>

      {filtered.length > PAGE_SIZE && (
        <div className="rt-pagination">
          <button disabled={safePage <= 1} onClick={() => setPage(1)}>«</button>
          <button disabled={safePage <= 1} onClick={() => setPage(p => p - 1)}>‹</button>
          <span className="rt-page-info">Page {safePage} of {totalPages} ({filtered.length} rows)</span>
          <button disabled={safePage >= totalPages} onClick={() => setPage(p => p + 1)}>›</button>
          <button disabled={safePage >= totalPages} onClick={() => setPage(totalPages)}>»</button>
        </div>
      )}
    </div>
  );
}
